import { useQuery } from '@tanstack/react-query';
import axios from 'axios';
import { ID } from 'types';

/**
 * The six buyer-facing groupings a SHAP explanation factor can belong to
 * (bidops-app `suggested_price_explanations` endpoint, mirroring
 * `suggested-price-service`'s `explain_constants.CATEGORY_KEYS`).
 */
export type SuggestedPriceExplanationCategory =
  | 'baseline_vs_supplier_pool'
  | 'event_setup'
  | 'supplier_competition'
  | 'supplier_pool_experience'
  | 'supplier_pool_pricing_history'
  | 'timing';

/**
 * Which way a factor pushed the price. Counter-intuitive by design: the model reports the effect
 * on the *discount*, so `POSITIVE` means it pushed the discount up and the suggested price down.
 * The drawer's group headings carry that meaning, which is why the sentence itself is
 * direction-agnostic.
 */
export const EXPLANATION_DIRECTION = {
  NEGATIVE: 'negative',
  POSITIVE: 'positive',
} as const;

export type SuggestedPriceExplanationDirection =
  typeof EXPLANATION_DIRECTION[keyof typeof EXPLANATION_DIRECTION];

export interface SuggestedPriceExplanationFactor {
  /**
   * Money figure for a currency-shaped factor, already converted into the event's currency but
   * carrying no symbol — the model service emits a bare number and the server deliberately does
   * not guess a symbol. Substitute it into `copy`'s `{{amount}}` token with the same currency
   * formatter the grid uses. Null for every factor whose sentence has no money in it.
   */
  amount: number | null;
  category_key: SuggestedPriceExplanationCategory;
  /** Resolved category label, already localized server-side — render as-is, no client label map. */
  category_label: string;
  /**
   * Localized, interpolated buyer-facing copy. Render as-is except for the `{{amount}}` token on
   * currency-shaped factors — see `amount`.
   */
  copy: string;
  direction: SuggestedPriceExplanationDirection;
}

export interface SuggestedPriceExplanationResponse {
  exchange_rate_date: string | null;
  /** Ranked, already capped server-side (currently at most 5) — can be empty. */
  factors: SuggestedPriceExplanationFactor[];
  line_item_id: number;
}

interface GetSuggestedPriceExplanationParams {
  eventId: ID;
  lineItemId: ID;
  lotId: ID;
  signal?: AbortSignal;
}

export const getSuggestedPriceExplanation = async ({
  eventId,
  lineItemId,
  lotId,
  signal,
}: GetSuggestedPriceExplanationParams): Promise<SuggestedPriceExplanationResponse> => {
  const response = await axios.get<SuggestedPriceExplanationResponse>(
    `/api/v1/events/${eventId}/lots/${lotId}/suggested_price_explanations/${lineItemId}`,
    { signal },
  );

  return response.data;
};

export const suggestedPriceExplanationQueryKey = (eventId: ID, lotId: ID, lineItemId: ID) => [
  'events',
  eventId,
  'lots',
  lotId,
  'suggested_price_explanations',
  lineItemId,
];

// SHAP recomputation is not free on the model service — avoid refetching on every refocus while
// the drawer stays open across a background tab switch.
const SUGGESTED_PRICE_EXPLANATION_STALE_TIME_MS = 5 * 60 * 1000;

interface UseGetSuggestedPriceExplanationParams {
  /** Gate — only fetch while the info drawer is actually open for this row. Never prefetch. */
  enabled: boolean;
  eventId: ID;
  lineItemId: ID;
  lotId: ID;
}

// One retry rather than TanStack's default three: a buyer is waiting on an open drawer, and three
// attempts with exponential backoff leaves them on a spinner for several seconds before the
// failure message appears. One covers a transient blip without stalling the failure state.
const SUGGESTED_PRICE_EXPLANATION_RETRY_COUNT = 1;

export const useGetSuggestedPriceExplanation = ({
  enabled,
  eventId,
  lineItemId,
  lotId,
}: UseGetSuggestedPriceExplanationParams) => useQuery({
  enabled,
  queryFn: ({ signal }) => getSuggestedPriceExplanation({
    eventId, lineItemId, lotId, signal,
  }),
  queryKey: suggestedPriceExplanationQueryKey(eventId, lotId, lineItemId),
  retry: SUGGESTED_PRICE_EXPLANATION_RETRY_COUNT,
  staleTime: SUGGESTED_PRICE_EXPLANATION_STALE_TIME_MS,
});
